import React from 'react';
import { QueryHookOptions } from '@apollo/client';
import { useQuery } from '@ts-gql/apollo';
import { OperationData, OperationVariables, gql } from '@ts-gql/tag';

import {
  GetClientLiteQueryVariables,
  useGetClientLite,
} from '../data/useGetClientLite';
import { useGetJobStatusList } from '../data/useGetJobStatusList';

const GET_JOB_AND_DEBTOR = gql`
  query GetJobAndDebtor($cloudId: String!) {
    getJob(cloudId: $cloudId) {
      cloudId
      name
      clientId
      jobStatusId
    }
  }
` as import('../../__generated__/ts-gql/GetJobAndDebtor').type;
export type GetJobAndDebtorVariables = OperationVariables<
  typeof GET_JOB_AND_DEBTOR
>;
export type GetJobAndDebtorResult = OperationData<typeof GET_JOB_AND_DEBTOR>;

export const useGetJobAndDebtor = (
  options: QueryHookOptions<GetJobAndDebtorResult, GetJobAndDebtorVariables>
) => {
  const statuses = useGetJobStatusList();
  const variables = options.variables as GetJobAndDebtorVariables;
  const jobRes = useQuery(GET_JOB_AND_DEBTOR, {
    context: { service: 'core' },
    variables,
    ...options,
  });

  const clientArgs: GetClientLiteQueryVariables = React.useMemo(() => {
    return {
      cloudId:
        jobRes &&
        jobRes.data &&
        jobRes.data.getJob &&
        jobRes.data.getJob.clientId
          ? jobRes.data.getJob.clientId
          : '',
    };
  }, [jobRes]);

  // I think this skip is legit, we're in control of this one
  const clientRes = useGetClientLite({
    context: { service: 'clients' },
    variables: clientArgs,
    skip: !clientArgs.cloudId,
  });

  return React.useMemo(() => {
    if (jobRes.loading || clientRes.loading || statuses.loading) {
      return { loading: true };
    }

    if (
      jobRes.error ||
      clientRes.error ||
      !jobRes.data ||
      !clientRes.data ||
      statuses.error
    ) {
      return { error: true };
    }

    const job = jobRes.data?.getJob;
    const status = statuses.data?.find((s) => s.cloudId === job?.jobStatusId);
    if (job && !status) {
      console.error(
        `Query Jobs Without Tasks: unable to find status "${job.jobStatusId}" for job "${job.cloudId}"`
      );
    }

    const data = job && {
      ...job,
      status: status!,
      client: clientRes.data,
    };

    return {
      data,
    };
  }, [jobRes, clientRes, statuses]);
};
